import { DuckDBAccessMode, ConsoleLogger } from '@duckdb/duckdb-wasm';
export { AsyncDuckDB, ConsoleLogger, VoidLogger, getJsDelivrBundles, selectBundle } from '@duckdb/duckdb-wasm';
import { DBStorageType, beginTransaction, withSavepoint, connect, getEnvironment } from '@proj-airi/duckdb-wasm';
export { connect, mapColumnData } from '@proj-airi/duckdb-wasm';
import { NoopLogger, entityKind, fillPlaceholders, isConfig, DefaultLogger, extractTablesRelationalConfig, createTableRelationsHelpers } from 'drizzle-orm';
import { PgSession, PgPreparedQuery, PgTransaction, PgDialect, PgDatabase } from 'drizzle-orm/pg-core';
import { readMigrationFiles } from 'drizzle-orm/migrator';

function isLiterallyTrue(value) {
  return typeof value === "string" && /^true$/i.test(value);
}
function parseDSN(dsn) {
  const structured = {
    scheme: "duckdb-wasm:"
  };
  const parsed = new URL(dsn);
  if (!parsed.protocol.startsWith("duckdb-wasm:")) {
    throw new Error(`Expected scheme to be "duckdb-wasm:" but got "${parsed.protocol}"`);
  }
  if (parsed.searchParams.get("bundles") === "import-url") {
    structured.bundles = "import-url";
  }
  const paramLogger = parsed.searchParams.get("logger");
  if (paramLogger && isLiterallyTrue(paramLogger)) {
    structured.logger = true;
  }
  const paramStorage = parsed.searchParams.get("storage");
  switch (paramStorage) {
    case DBStorageType.ORIGIN_PRIVATE_FS: {
      if (parsed.host.length > 0) {
        console.warn(`Host "${parsed.host}" will be ignored while using Origin Private FS`);
      }
      const paramWrite = parsed.searchParams.get("write");
      structured.storage = {
        type: DBStorageType.ORIGIN_PRIVATE_FS,
        path: parsed.pathname.startsWith("/") ? parsed.pathname.slice(1) : parsed.pathname,
        ...paramWrite && isLiterallyTrue(paramWrite) && {
          accessMode: DuckDBAccessMode.READ_WRITE
        }
      };
      break;
    }
    case null:
      break;
    default:
      console.warn(`Unknown storage type "${paramStorage}"`);
      break;
  }
  return structured;
}
function buildDSN(structured) {
  const parsed = new URL("duckdb-wasm:///");
  if (structured.bundles === "import-url") {
    parsed.searchParams.set("bundles", "import-url");
  }
  if (structured.logger) {
    parsed.searchParams.set("logger", "true");
  }
  if (structured.storage) {
    parsed.searchParams.set("storage", structured.storage.type);
    switch (structured.storage.type) {
      case DBStorageType.ORIGIN_PRIVATE_FS:
        parsed.pathname = structured.storage.path;
        if (!parsed.pathname.startsWith("/")) {
          parsed.pathname = `/${parsed.pathname}`;
        }
        if (structured.storage.accessMode === DuckDBAccessMode.READ_WRITE) {
          parsed.searchParams.set("write", "true");
        }
        break;
      case DBStorageType.NODE_FS:
        parsed.pathname = structured.storage.path;
        if (structured.storage.accessMode === DuckDBAccessMode.READ_WRITE) {
          parsed.searchParams.set("write", "true");
        }
        break;
    }
  }
  return parsed.toString();
}

class DuckDBWASMPreparedQuery extends PgPreparedQuery {
  constructor(client, queryString, params, logger, fields, customResultMapper) {
    super({ sql: queryString, params });
    this.client = client;
    this.queryString = queryString;
    this.params = params;
    this.logger = logger;
    this.fields = fields;
    this.customResultMapper = customResultMapper;
  }
  static [entityKind] = "DuckDBWasmPreparedQuery";
  async execute(placeholderValues = {}) {
    const params = fillPlaceholders(this.params, placeholderValues);
    this.logger.logQuery(this.queryString, params);
    const { fields, queryString: query, client, customResultMapper } = this;
    const c = await client;
    if (!fields && !customResultMapper) {
      return c.query(query, params);
    }
    return c.query(query, params);
  }
  async all(placeholderValues = {}) {
    const params = fillPlaceholders(this.params, placeholderValues);
    this.logger.logQuery(this.queryString, params);
    const c = await this.client;
    return c.query(this.queryString, params);
  }
}
class DuckDBWasmSession extends PgSession {
  constructor(client, dialect, schema, options = {}) {
    super(dialect);
    this.client = client;
    this.schema = schema;
    this.options = options;
    this.logger = options.logger ?? new NoopLogger();
  }
  static [entityKind] = "DuckDBWasmSession";
  logger;
  prepareQuery(query, fields, _name, _isResponseInArrayMode, customResultMapper) {
    return new DuckDBWASMPreparedQuery(
      this.client,
      query.sql,
      query.params,
      this.logger,
      fields,
      customResultMapper
    );
  }
  async query(query, params) {
    this.logger.logQuery(query, params);
    const c = await this.client;
    return c.query(query, params);
  }
  async queryObjects(query, params) {
    this.logger.logQuery(query, params);
    const c = await this.client;
    return c.query(query, params);
  }
  transaction(transaction, config) {
    return beginTransaction(this.client, async (client) => {
      const session = new DuckDBWasmSession(
        client,
        this.dialect,
        this.schema,
        this.options
      );
      const tx = new DuckDBWasmTransaction(this.dialect, session, this.schema);
      if (config) {
        await tx.setTransaction(config);
      }
      return transaction(tx);
    });
  }
}
class DuckDBWasmTransaction extends PgTransaction {
  static [entityKind] = "DuckDBWasmTransaction";
  dialect;
  session;
  constructor(dialect, session, schema, nestedIndex = 0) {
    super(dialect, session, schema, nestedIndex);
    this.dialect = dialect;
    this.session = session;
  }
  async transaction(transaction) {
    return withSavepoint(this.session.client, "", async (client) => {
      const session = new DuckDBWasmSession(
        client,
        this.dialect,
        this.schema,
        this.session.options
      );
      const tx = new DuckDBWasmTransaction(this.dialect, session, this.schema);
      return transaction(tx);
    });
  }
}

class DuckDBWasmDatabase extends PgDatabase {
  static [entityKind] = "DuckDBWasmDatabase";
}
function construct(client, config = {}) {
  const dialect = new PgDialect({ casing: config.casing });
  let logger;
  if (config.logger === true) {
    logger = new DefaultLogger();
  } else if (config.logger !== false) {
    logger = config.logger;
  }
  let schema;
  if (config.schema) {
    const tablesConfig = extractTablesRelationalConfig(
      config.schema,
      createTableRelationsHelpers
    );
    schema = {
      fullSchema: config.schema,
      schema: tablesConfig.tables,
      tableNamesMap: tablesConfig.tableNamesMap
    };
  }
  const session = new DuckDBWasmSession(client, dialect, schema, { logger });
  const db = new DuckDBWasmDatabase(dialect, session, schema);
  db.$client = client;
  return db;
}
async function getBundles(importUrl = false) {
  const env = await getEnvironment();
  switch (env) {
    case "browser":
      return importUrl ? (await import('@proj-airi/duckdb-wasm/bundles/import-url-browser')).getImportUrlBundles() : (await import('@proj-airi/duckdb-wasm/bundles/default-browser')).getBundles();
    case "node":
      return importUrl ? await (await import('@proj-airi/duckdb-wasm/bundles/import-url-node')).getImportUrlBundles() : await (await import('@proj-airi/duckdb-wasm/bundles/default-node')).getBundles();
    default:
      throw new Error(`Unsupported environment: "${env}"`);
  }
}
function constructByDSN(dsn, drizzleConfig) {
  const structured = parseDSN(dsn);
  return construct(connect({
    bundles: getBundles(structured.bundles === "import-url"),
    logger: structured.logger ? new ConsoleLogger() : void 0,
    storage: structured.storage
  }), drizzleConfig);
}
function drizzle(...params) {
  if (typeof params[0] === "string") {
    return constructByDSN(params[0], params[1]);
  }
  if (isConfig(params[0])) {
    const {
      connection,
      client,
      ...drizzleConfig
    } = params[0];
    if (client)
      return construct(client, drizzleConfig);
    if (typeof connection === "string")
      return constructByDSN(connection, drizzleConfig);
    if (typeof connection === "undefined")
      throw new Error("connection option is required");
    return construct(connect({
      bundles: connection.bundles,
      logger: connection.logger,
      storage: connection.storage
    }), drizzleConfig);
  }
  return construct(params[0], params[1]);
}
((drizzle2) => {
  function mock(config) {
    return construct({
      options: {
        parsers: {},
        serializers: {}
      }
    }, config);
  }
  drizzle2.mock = mock;
})(drizzle || (drizzle = {}));

async function migrate(db, config) {
  const migrations = readMigrationFiles(config);
  await db.dialect.migrate(migrations, db.session, config);
}

export { DuckDBWASMPreparedQuery, DuckDBWasmDatabase, DuckDBWasmSession, DuckDBWasmTransaction, buildDSN, drizzle, isLiterallyTrue, migrate, parseDSN };
